// The phrase a queue row shows. `top_up` stores a scoring formula on `reason`
// and the Clay name on `signal`; a strategist request has only the reason they typed.

import type { ApprovalPick, StrategistOption } from '../api/types';

export function pickSignal(pick: ApprovalPick): string {
  if (pick.signal && pick.signal.trim()) return pick.signal.trim();
  if (pick.requested_by) return pick.reason || 'Strategist request';
  const reason = pick.reason || '';
  if (reason.includes(';')) {
    const phrase = reason.slice(reason.lastIndexOf(';') + 1).trim();
    if (phrase && !phrase.toLowerCase().startsWith('fit ')) return phrase;
  }
  return reason;
}

export function pickKindLabel(
  pick: ApprovalPick,
  strategists: StrategistOption[] = [],
): string {
  if (!pick.requested_by) return 'Morning pick';
  const owner = strategistLabel(pick.requested_by, strategists);
  // `requested_by` is whose request it is. Name the asker too when they are someone
  // else, so the approver knows who to ask about it.
  const asker = pick.asked_by || pick.requested_by;
  if (asker.toLowerCase() === pick.requested_by.toLowerCase()) return `Requested by ${owner}`;
  return `Requested by ${strategistLabel(asker, strategists)} for ${owner}`;
}

export function strategistLabel(
  email: string,
  strategists: StrategistOption[],
): string {
  const match = strategists.find(row => row.email.toLowerCase() === email.toLowerCase());
  if (match?.name) return match.name;
  const local = email.split('@')[0] || email;
  const first = local.split('.')[0] || local;
  return first ? first.slice(0, 1).toUpperCase() + first.slice(1) : email;
}

// The writer stores a request's signal as "requested by <email>[ for <email>]", so the
// raw emails would sit in the Signal column. Show each one as the strategist's name.
const EMAIL_PATTERN = /[^\s@()]+@[^\s@()]+\.[a-z]{2,}/gi;

export function pickSignalLabel(
  pick: ApprovalPick,
  strategists: StrategistOption[] = [],
): string {
  const signal = pickSignal(pick);
  if (!pick.requested_by) return signal;
  return signal.replace(EMAIL_PATTERN, email => strategistLabel(email, strategists));
}

// Source codes the writers store, as a person would say them. Anything unlisted
// (clay, csv, conference, …) is already a plain word and shows as is.
const SOURCE_LABEL: Record<string, string> = {
  strategist_request: 'Request',
};

export function sourceLabel(source: string | null | undefined): string {
  if (!source) return '—';
  return SOURCE_LABEL[source] || source;
}
